import {
  Checkbox,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { ReactNode, useCallback, useMemo } from 'react';

import { useListbox } from './ListboxContext';
import { ListboxDataItem, ListboxItemRootProps } from './ListboxTypes';

function ListboxItemRoot({
  item,
  onSelect,
  beforeContentComponent,
  className,
  dark,
  color = FontColorEnum.Neutral,
}: ListboxItemRootProps) {
  const { selected, mode, parentCanBeChecked } = useListbox();
  const isSelected = useMemo(() => selected.includes(item.value), [item.value, selected]);

  const isItemChecked = useCallback((item: ListboxDataItem, selected: string[]): boolean => {
    if (selected.includes(item.value)) {
      return true;
    }

    if (item.children) {
      return item.children.some(child => isItemChecked(child, selected));
    }

    return false;
  }, []);

  const isChecked = useMemo(() => {
    if (item.isSelectAll && item.childrenIds && item.childrenIds.length > 0) {
      if (item.childrenIds.some(id => selected.includes(id))) {
        return true;
      }
      return item.childrenIds.every(id => selected.includes(id));
    }

    return isItemChecked(item, selected);
  }, [isItemChecked, item, selected]);

  const isItemIndeterminate = useCallback(
    (listItem: ListboxDataItem, selected: string[]): boolean => {
      if (listItem.isSelectAll && listItem.childrenIds && listItem.childrenIds.length > 0) {
        if (listItem.childrenIds.every(id => selected.includes(id))) {
          return false;
        }
        return listItem.childrenIds.some(id => selected.includes(id));
      }

      if (!listItem.children) {
        return false;
      }

      const someChildrenChecked = listItem.children.some(child => isItemChecked(child, selected));
      const allChildrenChecked = listItem.children.every(child => isItemChecked(child, selected));

      return someChildrenChecked && !allChildrenChecked;
    },
    [isItemChecked]
  );

  const isIndeterminate = useMemo(
    () => isItemIndeterminate(item, selected),
    [isItemIndeterminate, item, selected]
  );

  const withIcon = (name: ReactNode) =>
    item.icon ? (
      <span className="flex min-w-0 grow items-center gap-1">
        <span className="flex shrink-0">{item.icon}</span>
        {name}
      </span>
    ) : (
      name
    );

  const renderCheckboxName = (weight: FontWeightEnum) =>
    withIcon(
      <Tooltip help={item.label} grow containerClasses="!block w-full min-w-0">
        <Typography
          color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
          fontSize={FontSizeEnum.Sm}
          weight={weight}
          className="line-clamp-1"
        >
          {item.label}
        </Typography>
      </Tooltip>
    );

  return (
    <div
      className={clsx(
        'relative mb-1 rounded',
        'flex items-center',
        'cursor-pointer select-none',
        'px-2 py-1.5',
        'hover:!bg-neutral-20 dark:hover:bg-white/10',
        'group',
        {
          '!dark:bg-white/5 !bg-neutral-20': isSelected,
        },
        className
      )}
      role="button"
      onChange={() => {
        onSelect && onSelect(item);
      }}
    >
      {beforeContentComponent}
      {mode === 'multiple' && parentCanBeChecked && item.children && item.children.length > 0 ? (
        <Checkbox
          id={item.value}
          checked={isSelected}
          labelClassName="w-full !max-w-full"
          onChange={() => {
            onSelect && onSelect(item);
          }}
          textColor={color}
        >
          {renderCheckboxName(isSelected ? FontWeightEnum.Medium : FontWeightEnum.Regular)}
        </Checkbox>
      ) : mode !== 'single' ? (
        <Checkbox
          id={item.value}
          checked={isChecked}
          indeterminate={isIndeterminate}
          labelClassName="w-full !max-w-full"
          onChange={() => {
            onSelect && onSelect(item);
          }}
          textColor={color}
        >
          {renderCheckboxName(isChecked ? FontWeightEnum.Medium : FontWeightEnum.Regular)}
        </Checkbox>
      ) : null}
      {mode === 'simple' && (
        <Tooltip help={item.label} grow>
          <Typography
            color={FontColorEnum.Dark10}
            fontSize={FontSizeEnum.Sm}
            weight={isChecked ? FontWeightEnum.Medium : FontWeightEnum.Regular}
          >
            {item.label}
          </Typography>
        </Tooltip>
      )}
      {mode === 'single' && (
        <div
          className="flex w-full items-center justify-between"
          onClick={() => onSelect && onSelect(item)}
        >
          {withIcon(
            <Tooltip help={item.label} grow>
              <Typography
                color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                fontSize={FontSizeEnum.Sm}
                weight={isChecked ? FontWeightEnum.Medium : FontWeightEnum.Regular}
              >
                {item.label}
              </Typography>
            </Tooltip>
          )}
          {item.image && (
            <img src={item.image} alt={item.label} className="h-6 w-10 object-contain" />
          )}
        </div>
      )}

      {mode !== 'single' && item.image && (
        <img src={item.image} alt={item.label} className="h-6 w-10 object-contain" />
      )}
    </div>
  );
}

export default ListboxItemRoot;
